import React, { useCallback } from 'react';
import { analyticsService } from '@common/analytics';
import { ANALYTICS_EVENTS } from '@common/analytics/AnalyticsEvents';
import PhoneVerificationBase from '../../shared/phone_verification_base/phone_verification_base';
import withPGTranslation from '../../../config/withPGTranslation';

interface PhoneVerificationProps {
  phoneNumber: string;
  consentToMarketingSms: boolean;
  onCodeSubmit: (verificationCode: string) => void;
  onResendClick?: () => void;
  onBack?: () => void;
  t: (key: string) => string;
  clearError?: () => void;
  loading?: boolean;
  error?: string | null;
  consentedToGDPR: boolean;
  logInByPhone: (phoneNumber: string, confirmationCode: string, consentedToGDPR: boolean, consentToMarketingSms: boolean, email: string, signupFlow?: string) => Promise<any>;
  signUpFlow?: string;
  savedTimerEndsAt?: string | null;
  onTimerEndsAtChange?: (timerEndsAt: string | null) => void;
  smsAttempts?: number;
  incrementSmsAttempts?: () => number;
}

/**
 * PhoneVerification component - Screen for entering SMS verification code
 * Displays 4-digit code input fields with automatic verification
 */
function PhoneVerification({
  phoneNumber,
  consentToMarketingSms,
  onCodeSubmit,
  onResendClick,
  onBack,
  t,
  clearError,
  loading: parentLoading = false,
  error: parentError,
  consentedToGDPR,
  logInByPhone,
  signUpFlow,
  savedTimerEndsAt,
  onTimerEndsAtChange,
  smsAttempts = 0,
  incrementSmsAttempts
}: PhoneVerificationProps) {
  // Handle code verification with special logic for registration
  const handleVerify = useCallback(async (verificationCode: string) => {
    try {
      // logInByPhone now returns Promise
      await logInByPhone(phoneNumber, verificationCode, consentedToGDPR, consentToMarketingSms, '', signUpFlow);
      // If successful - code will be handled through authComplete in registration.js
    } catch (err: any) {
      // Check if it's no_backup_email operationStatus (email required) - navigate to email screen
      if (err && (err.operationStatus === 'no_backup_email' || err.message === 'EMAIL_REQUIRED')) {
        // Phone number is verified successfully, but backup email is required
        // Track phone number verified event with click source
        analyticsService.trackEvent(ANALYTICS_EVENTS.PHONE_NUMBER_VERIFIED, {
          'click source': 'signup'
        });

        // Call onCodeSubmit to trigger navigation to email screen
        if (onCodeSubmit) {
          onCodeSubmit(verificationCode);
        }
        // Don't throw error - navigation will happen
        return;
      }

      // For 422 with "Invalid confirmation code" or rate limit (timer_ends_at) - extract timer so resend modal shows correct cooldown
      // authHandler spreads errors onto rejected error, so timerEndsAt can be top-level or under .errors
      const timerEndsAtFromError = err?.timerEndsAt?.[0] ?? err?.errors?.timerEndsAt?.[0] ?? null;
      if (timerEndsAtFromError && onTimerEndsAtChange) {
        onTimerEndsAtChange(timerEndsAtFromError);
      }

      // Re-throw to be handled by base component (displays error message, stays on verification screen)
      throw err;
    }
  }, [logInByPhone, phoneNumber, consentedToGDPR, signUpFlow, onCodeSubmit, onTimerEndsAtChange]);

  return (
    <PhoneVerificationBase
      phoneNumber={ phoneNumber }
      onVerify={ handleVerify }
      onResendClick={ onResendClick }
      onBack={ onBack }
      onEditNumber={ onBack }
      t={ t }
      clearError={ clearError }
      loading={ parentLoading }
      error={ parentError }
      savedTimerEndsAt={ savedTimerEndsAt }
      onTimerEndsAtChange={ onTimerEndsAtChange }
      titleKey="smsVerification.verify_your_phone_number"
      smsAttempts={ smsAttempts }
      incrementSmsAttempts={ incrementSmsAttempts }
      clickSource="signup"
    />
  );
}

export default withPGTranslation(PhoneVerification);
